// Webfont CSS for the downloadable article frame.
//
// html-to-image renders through an SVG foreignObject, which cannot reach the
// page's fonts, so every face has to be inlined as a data URL. Left to itself
// it walks document.styleSheets and inlines EVERY @font-face it finds - which
// here means all of Google Fonts (Roboto, Inter's variable + italic axes,
// Prata, Lora, DM Serif Text, IBM Plex Mono/Sans, each split into unicode
// range subsets) plus bootstrap-icons and uicons. That is ~125 requests and
// several MB per download, none of which the frame uses.
//
// So we hand it `fontEmbedCSS` covering just the two faces a given language
// needs. The files are local bundle assets, fetched once and cached here.

import lohit_telugu from "../assets/fonts/Lohit-Telugu.ttf";
import lohit_tamil from "../assets/fonts/Lohit-Tamil.ttf";
import lohit_devanagari from "../assets/fonts/Lohit-Devanagari.ttf";
import lohit_bengali from "../assets/fonts/Lohit-Bengali.ttf";
import lohit_gujarati from "../assets/fonts/Lohit-Gujarati.ttf";
import ramabhadra from "../assets/fonts/Ramabhadra_Regular.ttf";
import noto_kannada from "../assets/fonts/NotoSansKannada-Regular.ttf";
import noto_malayalam from "../assets/fonts/NotoSansMalayalam-Regular.ttf";
import noto_tamil_semibold from "../assets/fonts/NotoSansTamil-SemiBold.ttf";
import roboto_regular from "../assets/fonts/Roboto-Regular.ttf";
import roboto_bold from "../assets/fonts/Roboto-Bold.ttf";

// The faces each language's heading and body rules in screenshotFrame.scss
// resolve to. Keep in step with that file.
const FRAME_FONTS = {
  telugu: [
    { family: "Ramabhadra Regular", url: ramabhadra },
    { family: "Lohit Telugu", url: lohit_telugu },
  ],
  tamil: [
    { family: "NotoSans Tamil SemiBold", url: noto_tamil_semibold },
    { family: "Lohit Tamil", url: lohit_tamil },
  ],
  kannada: [{ family: "NotoSans Kannada", url: noto_kannada }],
  malayalam: [{ family: "NotoSans Malayalam", url: noto_malayalam }],
  hindi: [{ family: "Lohit Devanagari", url: lohit_devanagari }],
  marathi: [{ family: "Lohit Devanagari", url: lohit_devanagari }],
  bengali: [{ family: "Lohit Bengali", url: lohit_bengali }],
  gujarati: [{ family: "Lohit Gujarati", url: lohit_gujarati }],
  english: [
    { family: "Roboto", url: roboto_regular, weight: 400 },
    { family: "Roboto", url: roboto_bold, weight: 700 },
  ],
};

// url -> data URL. Survives for the page session, so repeat downloads of the
// same language cost no requests at all.
const dataUrlCache = new Map();

const toDataUrl = async (url) => {
  if (dataUrlCache.has(url)) return dataUrlCache.get(url);

  const pending = (async () => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Could not load font ${url}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  })();

  // Cache the promise, not the result, so parallel callers share one request.
  dataUrlCache.set(url, pending);
  try {
    return await pending;
  } catch (error) {
    dataUrlCache.delete(url);
    throw error;
  }
};

/**
 * @font-face CSS for one frame language, with the font files inlined.
 * Returns "" if the faces cannot be loaded, which leaves html-to-image to
 * render with a fallback rather than failing the whole download.
 */
export const getFrameFontCSS = async (language) => {
  const fonts = FRAME_FONTS[language] || FRAME_FONTS.telugu;
  try {
    const faces = await Promise.all(
      fonts.map(async ({ family, url, weight }) => {
        const dataUrl = await toDataUrl(url);
        return `@font-face{font-family:'${family}';src:url(${dataUrl}) format('truetype');font-weight:${
          weight || "normal"
        };font-style:normal;font-display:block;}`;
      })
    );
    return faces.join("\n");
  } catch (error) {
    console.error("Error @ getFrameFontCSS", error);
    return "";
  }
};
